/**
 * Vue曝光指令
 * 当元素进入视口时触发回调函数
 * 
 * 使用方法：
 * v-exposure="callback"
 * v-exposure="{ handler: callback, threshold: 0.5, once: true }"
 */

// 存储观察器实例的WeakMap
const observerMap = new WeakMap()

// 默认配置
const defaultOptions = {
  threshold: 0.01, // 元素1%可见时触发
  rootMargin: '0px',
  once: true // 是否只触发一次
}

/**
 * 创建Intersection Observer
 * @param {HTMLElement} el 目标元素
 * @param {Object} binding 指令绑定信息
 * @param {Object} options 观察器选项
 */
function createObserver(el, binding, options) {
  const callback = (entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        // 元素曝光时执行回调
        if (typeof binding.value === 'function') {
          binding.value(entry, el)
        } else if (binding.value && typeof binding.value.handler === 'function') {
          binding.value.handler(entry, el)
        }

        // 如果设置了只触发一次，则断开观察
        if (options.once) {
          observer.unobserve(el)
          observerMap.delete(el)
        }
      }
    })
  }

  const observer = new IntersectionObserver(callback, {
    threshold: options.threshold,
    rootMargin: options.rootMargin
  })

  observer.observe(el)
  observerMap.set(el, observer)
}

/**
 * 解析指令参数
 * @param {*} value 指令值
 * @returns {Object} 解析后的配置
 */
function parseOptions(value) {
  if (typeof value === 'function') {
    return { ...defaultOptions, handler: value }
  }

  if (typeof value === 'object' && value !== null) {
    return {
      ...defaultOptions,
      ...value
    }
  }

  console.warn('[v-exposure] 指令值必须是函数或配置对象')
  return defaultOptions
}

export default {
  name: 'exposure',
  
  mounted(el, binding) {
    // 检查浏览器是否支持Intersection Observer
    if (typeof IntersectionObserver === 'undefined') {
      console.warn('[v-exposure] 当前浏览器不支持Intersection Observer API')
      return
    }

    const options = parseOptions(binding.value)
    createObserver(el, binding, options)
  },

  updated(el, binding) {
    // 如果绑定值发生变化，重新创建观察器
    if (binding.value !== binding.oldValue) {
      const observer = observerMap.get(el)
      if (observer) {
        observer.unobserve(el)
        observerMap.delete(el)
      }

      const options = parseOptions(binding.value)
      createObserver(el, binding, options)
    }
  },

  unmounted(el) {
    // 组件卸载时清理观察器
    const observer = observerMap.get(el)
    if (observer) {
      observer.unobserve(el)
      observerMap.delete(el)
    }
  }
}
